/* eslint-disable no-nested-ternary */
import { useUser } from '@folklore/auth';
import { useUrlGenerator } from '@folklore/routes';
import classNames from 'classnames';
import { useCallback, useMemo, useRef, useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { Link } from 'wouter';

import usePage from '../../hooks/usePage';

import { useContest } from '../../contexts/ContestContext';
import { useThemeClassName, useTheme } from '../../contexts/ThemeContext';
import StyledButton from '../buttons/StyledButton';
import EmailForm from '../forms/Email';
import DrawCard from '../items/DrawCard';
import PageTitle from '../partials/PageTitle';
import ParticipationsStats from '../partials/ParticipationsStats';
import Prizes from '../partials/Prizes';
import TodaysPoints from '../partials/TodaysPoints';

import styles from '../../../styles/pages/account-default.module.scss';

interface AccountProps {
    page: AccountPage | null;
    drawsSection?: React.ReactNode;
    prizesSection?: React.ReactNode;
    className: string;
}

function AccountDefault({
    page,
    drawsSection = null,
    prizesSection = null,
    className,
}: AccountProps) {
    const { refetch: reload } = usePage();
    const { name: themeName, isVariant } = useTheme();
    const themeClassName = useThemeClassName(styles);
    const {
        outcome: { hasPoints = false, hasLeaderboard = false },
    } = useContest();
    const intl = useIntl();
    const user = useUser();
    const { stats, draws } = user || {};
    const route = useUrlGenerator();

    const [, setEmailComplete] = useState(false);

    const onEmailComplete = useMemo(() => {
        setEmailComplete(true);
    }, [setEmailComplete]);

    const {
        title,
        description,
        withEmailForm = false,
        withParticipationsStats = false,
        emailFormTitle = null,
        emailFormDescription = null,
        prizes = null,
        prizesSectionTitle = null,
        prizesSectionDescription = null,
    } = page || {};

    const prizesCount = (prizes || []).length;

    // TODO: fix leaderboard positions and total
    // Must be outside of user stats

    const {
        prizes: totalPrizesCount = 0,
        pointsToday = null,
        leaderboardPosition = null,
        leaderboardTotal = null,
    } = stats || {};

    const prizesPositionRef = useRef(null);

    const onPrizeClaim = useCallback(() => {
        const { offsetTop } = prizesPositionRef.current;
        setTimeout(() => {
            window.scroll({
                top: offsetTop - 20,
                behavior: 'smooth',
            });
        }, 250);
        if (reload !== null) {
            reload();
        }
    }, [reload]);

    const emailForm = withEmailForm ? (
        <div className={styles.emailFormContainer}>
            <div className={styles.emailFormInner}>
                {emailFormTitle !== null ? (
                    <h2 className={styles.emailTitle}>{emailFormTitle}</h2>
                ) : null}
                <div className={styles.emailDescription}>
                    {emailFormDescription || (
                        <FormattedMessage
                            defaultMessage="Enter your email address for an additional chance to win!"
                            description="NHL Email form description"
                        />
                    )}
                </div>
                <EmailForm
                    className={styles.emailForm}
                    placeholder={intl.formatMessage({
                        defaultMessage: 'email@johndoe.com',
                        description: 'NHL Email form placeholder',
                    })}
                    onSuccess={onEmailComplete}
                    inputClassName={styles.emailInput}
                    inputContainerClassName={styles.emailFormInputContainer}
                    submitClassName={styles.emailSubmit}
                />
            </div>
        </div>
    ) : null;

    return (
        <div className={classNames([styles.container, themeClassName, className])}>
            <div className={styles.header}>
                <PageTitle className={styles.pageTitle} title={title} />
                {description !== null ? (
                    <div className={styles.description}>{description}</div>
                ) : null}
            </div>
            {leaderboardPosition !== null && leaderboardTotal !== null ? (
                <div className={styles.leaderboardPosition}>
                    <Link href={route('leaderboard')}>
                        <FormattedMessage
                            defaultMessage="Your rank : {position}/{total}"
                            description="Leaderboard position"
                            values={{
                                position: leaderboardPosition,
                                total: leaderboardTotal,
                            }}
                        />
                    </Link>
                </div>
            ) : null}
            {hasPoints && pointsToday !== null && !hasLeaderboard ? (
                <TodaysPoints className={styles.todaysGamePoints} points={pointsToday} />
            ) : null}

            {themeName === 'voisin' ? emailForm : null}

            {drawsSection === null && draws !== null && draws.length > 0 ? (
                <div className={styles.draws}>
                    {draws.map((draw, drawIndex) => (
                        <div key={`draw-${drawIndex + 1}`} className={styles.draw}>
                            <DrawCard draw={draw} className={styles.drawCard} />
                        </div>
                    ))}
                </div>
            ) : (
                drawsSection
            )}

            {prizesSection === null && prizes !== null && prizes.length > 0 ? (
                <div className={styles.prizesContainer}>
                    <div ref={prizesPositionRef} />
                    {prizesSectionTitle !== null ? (
                        <h4 className={styles.sectionTitle}>{prizesSectionTitle}</h4>
                    ) : null}
                    {prizesSectionDescription !== null ? (
                        <div
                            className={styles.sectionDescription}
                            dangerouslySetInnerHTML={{ __html: prizesSectionDescription }}
                        />
                    ) : null}
                    <Prizes
                        prizes={prizes}
                        className={styles.prizes}
                        onPrizeClaim={onPrizeClaim}
                        withTitle={themeName === 'coorslive'}
                        withoutHeader={isVariant('normandin')}
                    />
                    {totalPrizesCount > prizesCount && prizesCount > 0 ? (
                        <div className={styles.allPrizesLinkContainer}>
                            <StyledButton className={styles.allPrizesLink} href={route('prizes')}>
                                <FormattedMessage
                                    defaultMessage="All your prizes"
                                    description="Button label"
                                />
                            </StyledButton>
                        </div>
                    ) : null}
                </div>
            ) : (
                prizesSection
            )}

            {withParticipationsStats ? (
                <div className={styles.statsContainer}>
                    <ParticipationsStats className={styles.stats} />
                </div>
            ) : null}

            {themeName !== 'voisin' ? emailForm : null}
        </div>
    );
}

export default AccountDefault;
